import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { narApi } from '../services/api';

const P   = '#6B3FA0';
const PD  = '#4A2B70';
const PL  = '#F3EEFA';
const PLM = '#E5D5F5';

const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB',
  borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
};
const btn = (bg, color, border) => ({
  padding: '7px 14px', backgroundColor: bg, color, border: border || 'none',
  borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
});
const lbl = { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#374151' };

const TABS = ['Questions', 'Tags', 'Grade Scale'];

export default function NarAdmin() {
  const [tab, setTab] = useState('Questions');

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: '#1a1a2e' }}>NAR Catalog Management</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: '14px' }}>
          Manage NAR types, questions, tags, and grade scale
        </p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: `2px solid ${PLM}`, marginBottom: '24px' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '10px 20px', border: 'none', borderBottom: tab === t ? `3px solid ${P}` : '3px solid transparent', backgroundColor: 'transparent', color: tab === t ? PD : '#6B7280', fontWeight: tab === t ? '700' : '500', fontSize: '14px', cursor: 'pointer', marginBottom: '-2px' }}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Questions'   && <QuestionsTab />}
      {tab === 'Tags'        && <TagsTab />}
      {tab === 'Grade Scale' && <GradeScaleTab />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// QUESTIONS TAB  — type > section > category > question tree
// ══════════════════════════════════════════════════════════════

function QuestionsTab() {
  const [types, setTypes]         = useState([]);
  const [selType, setSelType]     = useState(null);
  const [structure, setStructure] = useState([]);
  const [allTags, setAllTags]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  // Inline editor state
  const [editSec,    setEditSec]    = useState(null);
  const [editCat,    setEditCat]    = useState(null);
  const [editQ,      setEditQ]      = useState(null);
  const [editQTags,  setEditQTags]  = useState(new Set()); // tag ids for question being edited
  const [newSec,     setNewSec]     = useState(false);
  const [newCat,     setNewCat]     = useState(null);
  const [newQ,       setNewQ]       = useState(null);

  const loadTypes = async () => {
    const res = await narApi.listTypes();
    const t = res.data.nar_types || [];
    setTypes(t);
    if (t.length && !selType) setSelType(t[0]);
  };

  const loadStructure = async (typeId) => {
    setLoading(true);
    try {
      const res = await narApi.getTypeStructure(typeId);
      setStructure(res.data.sections || []);
    } catch { toast.error('Failed to load structure'); }
    finally { setLoading(false); }
  };

  const loadTags = async () => {
    try {
      const res = await narApi.listTags();
      setAllTags(res.data.tags || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { loadTypes(); loadTags(); }, []);
  useEffect(() => { if (selType) loadStructure(selType.id); }, [selType]);

  const reload = () => selType && loadStructure(selType.id);

  const openEditQ = (q) => {
    setEditQ(q);
    setEditQTags(new Set((q.tags || []).map(t => t.id)));
  };

  const toggleEditQTag = (tid) => setEditQTags(prev => {
    const next = new Set(prev);
    next.has(tid) ? next.delete(tid) : next.add(tid);
    return next;
  });

  // Section save
  const saveSec = async (sec, data) => {
    try {
      if (sec) {
        await narApi.updateSection(sec.id, { ...sec, ...data });
        toast.success('Section updated');
      } else {
        await narApi.createSection({ nar_type_id: selType.id, ...data });
        toast.success('Section created');
      }
      setEditSec(null); setNewSec(false); reload();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
  };

  // Category save
  const saveCat = async (cat, section_id, data) => {
    try {
      if (cat) {
        await narApi.updateCategory(cat.id, { ...cat, ...data });
        toast.success('Category updated');
      } else {
        await narApi.createCategory({ section_id, ...data });
        toast.success('Category created');
      }
      setEditCat(null); setNewCat(null); reload();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
  };

  // Question save — also saves tags
  const saveQ = async (q, category_id, data) => {
    try {
      if (q) {
        await narApi.updateQuestion(q.id, { ...q, ...data });
        await narApi.setQuestionTags(q.id, [...editQTags]);
        toast.success('Question updated');
      } else {
        await narApi.createQuestion({ category_id, ...data });
        toast.success('Question created');
      }
      setEditQ(null); setEditQTags(new Set()); setNewQ(null); reload();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file || !selType) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await narApi.importQuestions(selType.id, fd);
      toast.success(`Imported ${res.data.created} questions`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      {/* Type selector + actions */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div>
          <label style={lbl}>NAR Type</label>
          <select value={selType?.id || ''} onChange={e => setSelType(types.find(t => t.id === Number(e.target.value)))}
            style={{ ...inputStyle, width: '220px' }}>
            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignSelf: 'flex-end' }}>
          <button onClick={() => setNewSec(true)} style={btn(PL, PD, `1px solid ${PLM}`)}>+ Section</button>
          <button onClick={() => narApi.downloadImportTemplate(selType?.id)} style={btn('white', '#374151', '1px solid #D1D5DB')}>⬇ Template</button>
          <button onClick={() => fileRef.current?.click()} disabled={importing} style={btn('#D1FAE5', '#065F46', '1px solid #A7F3D0')}>
            {importing ? 'Importing…' : '⬆ Import XLSX'}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx" onChange={handleImport} style={{ display: 'none' }} />
        </div>
      </div>

      {/* New section form */}
      {newSec && (
        <InlineForm
          title="New Section"
          fields={[
            { key: 'name', label: 'Name', type: 'text', required: true },
            { key: 'weight', label: 'Weight (1–5)', type: 'number', default: 3, min: 1, max: 5 },
            { key: 'display_order', label: 'Display Order', type: 'number', default: 0 },
          ]}
          onSave={d => saveSec(null, d)}
          onCancel={() => setNewSec(false)}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>Loading…</div>
      ) : (
        <div>
          {structure.map(sec => (
            <div key={sec.id} style={{ marginBottom: '16px', border: `1px solid ${PLM}`, borderRadius: '8px', overflow: 'hidden' }}>
              {/* Section header */}
              <div style={{ backgroundColor: PD, color: 'white', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {editSec?.id === sec.id ? (
                  <InlineForm
                    dark
                    fields={[
                      { key: 'name', label: 'Name', type: 'text', required: true, default: sec.name },
                      { key: 'weight', label: 'Weight', type: 'number', default: sec.weight, min: 1, max: 5 },
                      { key: 'display_order', label: 'Order', type: 'number', default: sec.display_order },
                    ]}
                    onSave={d => saveSec(sec, d)}
                    onCancel={() => setEditSec(null)}
                  />
                ) : (
                  <>
                    <span style={{ fontWeight: '700', fontSize: '14px' }}>{sec.name} <span style={{ fontSize: '12px', opacity: 0.7 }}>wt:{sec.weight}</span></span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => setNewCat(sec.id)} style={{ ...btn(PLM, PD), fontSize: '12px' }}>+ Category</button>
                      <button onClick={() => setEditSec(sec)} style={{ ...btn('rgba(255,255,255,0.15)', 'white'), fontSize: '12px' }}>Edit</button>
                    </div>
                  </>
                )}
              </div>

              {/* New category form */}
              {newCat === sec.id && (
                <div style={{ padding: '12px 16px', backgroundColor: '#FDFBFF', borderBottom: `1px solid ${PLM}` }}>
                  <InlineForm
                    title="New Category"
                    fields={[
                      { key: 'name', label: 'Name', type: 'text', required: true },
                      { key: 'weight', label: 'Weight', type: 'number', default: 3, min: 1, max: 5 },
                      { key: 'display_order', label: 'Order', type: 'number', default: 0 },
                    ]}
                    onSave={d => saveCat(null, sec.id, d)}
                    onCancel={() => setNewCat(null)}
                  />
                </div>
              )}

              {/* Categories */}
              {(sec.categories || []).map(cat => (
                <div key={cat.id}>
                  <div style={{ backgroundColor: PL, padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${PLM}` }}>
                    {editCat?.id === cat.id ? (
                      <InlineForm
                        fields={[
                          { key: 'name', label: 'Name', type: 'text', required: true, default: cat.name },
                          { key: 'weight', label: 'Weight', type: 'number', default: cat.weight, min: 1, max: 5 },
                        ]}
                        onSave={d => saveCat(cat, sec.id, d)}
                        onCancel={() => setEditCat(null)}
                      />
                    ) : (
                      <>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: PD }}>{cat.name} <span style={{ fontSize: '11px', opacity: 0.6 }}>wt:{cat.weight}</span></span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => setNewQ(cat.id)} style={{ ...btn('white', PD, `1px solid ${PLM}`), fontSize: '12px' }}>+ Question</button>
                          <button onClick={() => setEditCat(cat)} style={{ ...btn('white', '#6B7280', '1px solid #D1D5DB'), fontSize: '12px' }}>Edit</button>
                        </div>
                      </>
                    )}
                  </div>

                  {/* New question form */}
                  {newQ === cat.id && (
                    <div style={{ padding: '12px 24px', backgroundColor: '#FDFBFF', borderBottom: '1px solid #F3EEFA' }}>
                      <InlineForm
                        title="New Question"
                        fields={[
                          { key: 'question_text', label: 'Question Text', type: 'textarea', required: true },
                          { key: 'weight', label: 'Weight', type: 'number', default: 3, min: 1, max: 5 },
                          { key: 'allow_na', label: 'Allow N/A', type: 'checkbox', default: true },
                          { key: 'answerable_remotely', label: 'Answerable Remotely', type: 'checkbox', default: false },
                          { key: 'micro_help', label: 'Micro Help', type: 'text' },
                          { key: 'display_order', label: 'Display Order', type: 'number', default: 0 },
                        ]}
                        onSave={d => saveQ(null, cat.id, d)}
                        onCancel={() => setNewQ(null)}
                      />
                    </div>
                  )}

                  {/* Questions */}
                  {(cat.questions || []).map((q, qi) => (
                    <div key={q.id} style={{ padding: '10px 24px', borderBottom: '1px solid #F9FAFB', backgroundColor: qi % 2 === 0 ? 'white' : '#FDFCFF' }}>
                      {editQ?.id === q.id ? (
                        <div>
                          <InlineForm
                            fields={[
                              { key: 'question_text', label: 'Question Text', type: 'textarea', required: true, default: q.question_text },
                              { key: 'weight', label: 'Weight', type: 'number', default: q.weight, min: 1, max: 5 },
                              { key: 'allow_na', label: 'Allow N/A', type: 'checkbox', default: !!q.allow_na },
                              { key: 'answerable_remotely', label: 'Answerable Remotely', type: 'checkbox', default: !!q.answerable_remotely },
                              { key: 'micro_help', label: 'Micro Help', type: 'text', default: q.micro_help || '' },
                              { key: 'active', label: 'Active', type: 'checkbox', default: !!q.active },
                            ]}
                            onSave={d => saveQ(q, cat.id, d)}
                            onCancel={() => { setEditQ(null); setEditQTags(new Set()); }}
                          />
                          {allTags.length > 0 && (
                            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #F3EEFA' }}>
                              <label style={{ ...lbl, marginBottom: '8px' }}>Tags</label>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {allTags.map(t => {
                                  const on = editQTags.has(t.id);
                                  return (
                                    <button
                                      key={t.id}
                                      type="button"
                                      onClick={() => toggleEditQTag(t.id)}
                                      style={{
                                        padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '600',
                                        cursor: 'pointer', transition: 'all 0.15s',
                                        backgroundColor: on ? t.color : t.color + '22',
                                        color: on ? 'white' : t.color,
                                        border: `1.5px solid ${t.color}`,
                                      }}
                                    >
                                      {t.name}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
                              <span style={{ color: '#9CA3AF', marginRight: '6px', fontSize: '11px' }}>{qi + 1}.</span>
                              {q.question_text}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px', alignItems: 'center' }}>
                              <span style={{ fontSize: '11px', color: '#9CA3AF' }}>wt:{q.weight}</span>
                              {q.allow_na && <span style={{ fontSize: '11px', color: '#9CA3AF' }}>N/A ok</span>}
                              {q.answerable_remotely && <span style={{ fontSize: '11px', backgroundColor: '#DBEAFE', color: '#1E40AF', padding: '0 5px', borderRadius: '8px' }}>Remote</span>}
                              {!q.active && <span style={{ fontSize: '11px', backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0 5px', borderRadius: '8px' }}>Inactive</span>}
                              {q.micro_help && <span style={{ fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic' }}>"{q.micro_help.slice(0, 40)}{q.micro_help.length > 40 ? '…' : ''}"</span>}
                              {(q.tags || []).map(t => (
                                <span key={t.id} style={{ fontSize: '11px', padding: '0 7px', borderRadius: '10px', backgroundColor: (t.color || P) + '22', color: t.color || P, border: `1px solid ${(t.color || P)}44`, fontWeight: '600' }}>
                                  {t.name}
                                </span>
                              ))}
                            </div>
                          </div>
                          <button onClick={() => openEditQ(q)} style={{ ...btn('white', '#6B7280', '1px solid #E5E7EB'), fontSize: '12px', flexShrink: 0 }}>Edit</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
          {structure.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              No sections yet. Add a section or import from XLSX.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TAGS TAB
// ══════════════════════════════════════════════════════════════

function TagsTab() {
  const [tags, setTags] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#6B3FA0' });

  const load = async () => {
    const res = await narApi.listTags();
    setTags(res.data.tags || []);
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (tag) => {
    try {
      if (tag) {
        await narApi.updateTag(tag.id, form);
        toast.success('Tag updated');
      } else {
        await narApi.createTag(form);
        toast.success('Tag created');
      }
      setEditId(null);
      setForm({ name: '', color: '#6B3FA0' });
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Delete tag "${tag.name}"?`)) return;
    await narApi.deleteTag(tag.id);
    toast.success('Tag deleted');
    load();
  };

  return (
    <div style={{ maxWidth: '600px' }}>
      <h2 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '700', color: PD }}>Tags</h2>

      {/* Add form */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '14px', color: PD }}>New Tag</h3>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Name</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={lbl}>Color</label>
            <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
              style={{ width: '44px', height: '34px', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer' }} />
          </div>
          <button onClick={() => handleSave(null)} style={btn(P, 'white')}>Add</button>
        </div>
      </div>

      {/* Tag list */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {tags.map(t => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '20px', backgroundColor: t.color + '22', border: `1.5px solid ${t.color}44` }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: t.color, display: 'inline-block' }} />
            <span style={{ fontSize: '13px', fontWeight: '600', color: PD }}>{t.name}</span>
            <button onClick={() => handleDelete(t)}
              style={{ background: 'none', border: 'none', color: '#DC2626', fontSize: '14px', cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}>×</button>
          </div>
        ))}
        {tags.length === 0 && <p style={{ color: '#9CA3AF', fontSize: '13px' }}>No tags yet.</p>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// GRADE SCALE TAB
// ══════════════════════════════════════════════════════════════

function GradeScaleTab() {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    narApi.getGradeScale().then(res => setRows(res.data.grade_scale || []));
  }, []);

  const handleChange = (idx, field, val) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await narApi.updateGradeScale(rows);
      toast.success('Grade scale saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: '640px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: PD }}>Grade Scale</h2>
        <button onClick={handleSave} disabled={saving} style={{ ...btn(P, 'white'), opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: PL, borderBottom: `2px solid ${PLM}` }}>
              {['Grade', 'Min Score (%)', 'Max Score (%)', 'Display Order'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: PD }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #F3F4F6', backgroundColor: i % 2 === 0 ? 'white' : '#FDFCFF' }}>
                <td style={{ padding: '8px 14px' }}>
                  <input value={r.grade} onChange={e => handleChange(i, 'grade', e.target.value)}
                    style={{ ...inputStyle, width: '60px', textAlign: 'center', fontWeight: '700', color: PD }} />
                </td>
                <td style={{ padding: '8px 14px' }}>
                  <input type="number" value={r.min_score} onChange={e => handleChange(i, 'min_score', e.target.value)}
                    style={{ ...inputStyle, width: '90px' }} />
                </td>
                <td style={{ padding: '8px 14px' }}>
                  <input type="number" value={r.max_score} onChange={e => handleChange(i, 'max_score', e.target.value)}
                    style={{ ...inputStyle, width: '90px' }} />
                </td>
                <td style={{ padding: '8px 14px' }}>
                  <input type="number" value={r.display_order} onChange={e => handleChange(i, 'display_order', e.target.value)}
                    style={{ ...inputStyle, width: '80px' }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// INLINE FORM  — reusable mini form for quick edits
// ══════════════════════════════════════════════════════════════

function InlineForm({ title, fields, onSave, onCancel, dark }) {
  const init = {};
  fields.forEach(f => {
    init[f.key] = f.default !== undefined ? f.default : (f.type === 'checkbox' ? false : '');
  });
  const [vals, setVals] = useState(init);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(vals);
  };

  const textColor = dark ? 'white' : '#374151';
  const inputOverride = dark
    ? { ...inputStyle, backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }
    : inputStyle;

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
      {title && <div style={{ width: '100%', fontSize: '12px', fontWeight: '700', color: textColor, marginBottom: '2px' }}>{title}</div>}
      {fields.map(f => (
        <div key={f.key} style={{ minWidth: f.type === 'textarea' ? '100%' : f.type === 'checkbox' ? 'auto' : '160px', flex: f.type === 'textarea' ? '1 1 100%' : '1 1 auto', maxWidth: '300px' }}>
          {f.type !== 'checkbox' && (
            <label style={{ ...lbl, color: dark ? 'rgba(255,255,255,0.8)' : '#374151' }}>{f.label}</label>
          )}
          {f.type === 'textarea' ? (
            <textarea value={vals[f.key]} onChange={e => setVals(v => ({ ...v, [f.key]: e.target.value }))}
              rows={3} required={f.required}
              style={{ ...inputOverride, resize: 'vertical', fontFamily: 'inherit' }} />
          ) : f.type === 'checkbox' ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: textColor }}>
              <input type="checkbox" checked={!!vals[f.key]}
                onChange={e => setVals(v => ({ ...v, [f.key]: e.target.checked }))} />
              {f.label}
            </label>
          ) : (
            <input type={f.type} value={vals[f.key]}
              onChange={e => setVals(v => ({ ...v, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value }))}
              required={f.required} min={f.min} max={f.max}
              style={inputOverride} />
          )}
        </div>
      ))}
      <div style={{ display: 'flex', gap: '6px', alignSelf: 'flex-end' }}>
        <button type="submit" style={dark ? btn('rgba(255,255,255,0.2)', 'white', '1px solid rgba(255,255,255,0.4)') : btn(P, 'white')}>
          Save
        </button>
        <button type="button" onClick={onCancel}
          style={dark ? btn('transparent', 'rgba(255,255,255,0.7)', '1px solid rgba(255,255,255,0.3)') : btn('white', '#6B7280', '1px solid #D1D5DB')}>
          Cancel
        </button>
      </div>
    </form>
  );
}
